<template>
    <page-controls>
        <template v-slot:left>
            <vx-button
                @click.prevent="
                    filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                    apply();
                "
                type="button"
                color="white"
                size="lg"
                shadow
            >
                <font-awesome-icon
                    widthAuto
                    :icon="filters.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                ></font-awesome-icon>
                <span class="ms-2">{{ filters.sort_dir === 'asc' ? 'Oldest first' : 'Newest first' }}</span>
            </vx-button>
        </template>
        <template v-slot:right>
            <div class="grow @3xl/controls:max-w-md">
                <date-range-selectable
                    :modelValue="dateRange"
                    @update:modelValue="handleDateRange"
                    :options="dateRangeOptions"
                    :formatRange="formatRange"
                />
            </div>
        </template>
    </page-controls>
</template>

<script lang="ts" setup>
import { computed, ref, watch, nextTick } from 'vue';
import { VxButton } from '@voxie/frontend-components';
import { faArrowDownWideShort, faArrowUpWideShort } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { Dayjs } from 'dayjs';
import PageControls from '~/components/general/PageControls.vue';
import DateRangeSelectable, {
    type DateRangeValue,
    type RangeOption,
} from '~/components/general/DateRangeSelectable.vue';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';

export interface StatusLogFilters {
    start_date: string;
    end_date: string;
    sort_dir: 'asc' | 'desc';
    cursor: string | null;
    per_page: number;
}

const props = defineProps<{
    modelValue: StatusLogFilters;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: StatusLogFilters): void;
    (e: 'apply'): void;
}>();

const filters = ref<StatusLogFilters>({ ...props.modelValue });

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    { deep: true }
);

const buildPresetRange = (days: number): Pick<RangeOption, 'start_date' | 'end_date'> => ({
    start_date: toUTCDateTimeFormat(
        dayjs()
            .startOf('day')
            .subtract(days - 1, 'day')
    ),
    end_date: toUTCDateTimeFormat(dayjs().endOf('day')),
});

const dateRangeOptions = computed<RangeOption[]>(() => [
    { value: 'last_7_days', label: 'Last 7 Days', ...buildPresetRange(7) },
    { value: 'last_14_days', label: 'Last 14 Days', ...buildPresetRange(14) },
    { value: 'last_30_days', label: 'Last 30 Days', ...buildPresetRange(30) },
    { value: 'last_90_days', label: 'Last 90 Days', ...buildPresetRange(90) },
    { value: 'custom', label: 'Custom Period' },
]);

const formatRange = (start: Dayjs, end: Dayjs) => ({
    start_date: toUTCDateTimeFormat(start.startOf('day')),
    end_date: toUTCDateTimeFormat(end.endOf('day')),
});

const dateRange = computed<DateRangeValue>(() => ({
    start_date: filters.value.start_date,
    end_date: filters.value.end_date,
}));

const handleDateRange = (range: DateRangeValue) => {
    filters.value = { ...filters.value, ...range };
    apply();
};

const apply = async () => {
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};
</script>
